/**
 * Review Submission Panel
 * 
 * Handles report submission for review with pre-submission validation,
 * review type selection, and re-submission workflows.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../stores/toast';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Send, 
  RefreshCw,
  FileCheck,
  Clock,
  X
} from 'lucide-react';
import { 
  submitForReview, 
  reSubmitForReview,
  getSubmissionStatus,
  validateReadiness,
  getAvailableReviewers,
  type ReviewSubmissionStatus,
  type ReviewType,
  type AvailableReviewer
} from '../../api/reviewApi';
import { useWorkspace } from '../../hooks/useWorkspace';
import { getPolicyPack } from '../../api/policy';
import { getProject } from '../../api/projects';

/** Extract user-friendly message from API error (400 detail, etc.) */
function getApiErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (data != null) {
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.detail === 'object' && Array.isArray(data.detail)) {
      return data.detail.map((d: any) => typeof d === 'string' ? d : d?.msg ?? JSON.stringify(d)).join('. ');
    }
    if (typeof data === 'string') return data;
  }
  return error?.message || 'Something went wrong. Please try again.';
}

/** True if the error is about pre-submission validation / QA requirements */
function isPreSubmissionValidationError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('pre-submission validation') || lower.includes('qa tab') || lower.includes('requirements met');
}

export interface ReviewSubmissionPanelProps {
  reportId: string;
  onSubmissionChange?: () => void;
  /** When set, panel can offer "Go to QA tab" after pre-submission validation errors */
  onSwitchToQATab?: () => void;
}

export function ReviewSubmissionPanel({ 
  reportId, 
  onSubmissionChange,
  onSwitchToQATab,
}: ReviewSubmissionPanelProps) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [selectedReviewType, setSelectedReviewType] = useState<ReviewType>('full');
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [revisionNotes, setRevisionNotes] = useState('');
  const [showReSubmitForm, setShowReSubmitForm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reSubmitError, setReSubmitError] = useState<string | null>(null);
  const [reviewTypes, setReviewTypes] = useState<Record<string, any>>({
    // Default review types (fallback if policy pack doesn't define them)
    full: { name: 'Full Review', description: 'Comprehensive review of all sections' },
    quick: { name: 'Quick Review', description: 'Rapid review of key sections' },
  });

  // Load policy pack to get review types
  useEffect(() => {
    if (!projectId) return;
    
    // First fetch project to get policy_pack_id
    getProject(projectId).then((project) => {
      if (project.policy_pack_id) {
        return getPolicyPack(project.policy_pack_id);
      }
      return null;
    }).then((pack) => {
      if (pack) {
        const reviewWorkflow = pack?.review_workflow;
        if (reviewWorkflow?.review_types) {
          setReviewTypes(reviewWorkflow.review_types);
        }
      }
    }).catch((err) => {
      console.warn('[Review Submission] Failed to load policy pack:', err);
    });
  }, [projectId]);

  // Get submission status
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['review-submission-status', reportId],
    queryFn: () => getSubmissionStatus(reportId),
    refetchOnWindowFocus: false,
  });

  // Derive isSubmitted from status (needed for query enabled condition)
  const isSubmitted = status?.submitted || false;

  // Get available reviewers for selected review type
  const { data: availableReviewers, isLoading: reviewersLoading, error: reviewersError } = useQuery({
    queryKey: ['available-reviewers', reportId, selectedReviewType],
    queryFn: () => getAvailableReviewers(reportId, selectedReviewType),
    enabled: !!reportId && !!selectedReviewType && !isSubmitted,
    retry: 1,
  });

  // Validate readiness
  const validateMutation = useMutation({
    mutationFn: () => validateReadiness(reportId),
    onSuccess: (validation) => {
      if (!validation.ready) {
        addToast({
          type: 'error',
          title: 'Report not ready for submission',
          message: validation.errors.join(' · '),
          duration: 8000,
        });
      } else {
        addToast({ type: 'success', message: 'Report is ready for submission.' });
      }
    },
  });

  // Submit for review
  const submitMutation = useMutation({
    mutationFn: () => submitForReview(reportId, selectedReviewType, submissionNotes || undefined, selectedReviewer || undefined),
    onSuccess: () => {
      setSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ['review-submission-status', reportId] });
      queryClient.invalidateQueries({ queryKey: ['review-history', reportId] });
      queryClient.invalidateQueries({ queryKey: ['review-assignments', reportId] });
      setSubmissionNotes('');
      setSelectedReviewer('');
      if (onSubmissionChange) {
        onSubmissionChange();
      }
      addToast({
        type: 'success',
        title: 'Submitted for review',
        message: 'The reviewer has been notified. You will receive an in-app notification when the review is complete.',
        duration: 7000,
      });
    },
    onError: (error: any) => {
      setSubmitError(getApiErrorMessage(error));
    },
  });

  // Re-submit for review
  const reSubmitMutation = useMutation({
    mutationFn: () => reSubmitForReview(reportId, revisionNotes),
    onSuccess: () => {
      setReSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ['review-submission-status', reportId] });
      queryClient.invalidateQueries({ queryKey: ['review-history', reportId] });
      setRevisionNotes('');
      setShowReSubmitForm(false);
      if (onSubmissionChange) {
        onSubmissionChange();
      }
      addToast({
        type: 'success',
        title: 'Re-submitted for review',
        message: 'Your revised report has been submitted. The reviewer has been notified.',
        duration: 7000,
      });
    },
    onError: (error: any) => {
      setReSubmitError(getApiErrorMessage(error));
    },
  });

  const handleValidate = () => {
    validateMutation.mutate();
  };

  const handleSubmit = () => {
    if (!selectedReviewType) {
      addToast({ type: 'error', message: 'Please select a review type before submitting.' });
      return;
    }
    setSubmitError(null);
    submitMutation.mutate();
  };

  const handleReSubmit = () => {
    setReSubmitError(null);
    reSubmitMutation.mutate();
  };

  if (statusLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading submission status...</span>
        </div>
      </Card>
    );
  }

  // isSubmitted is already defined above for the query enabled condition
  const isReady = status?.ready_for_submission || false;
  const isReadyForReSubmit = status?.ready_for_re_submission || false;
  const validation = status?.submission_validation || { ready: false, errors: [], warnings: [] };

  return (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Review Submission</h3>
        {status && (
          <div className="flex items-center gap-4 text-sm">
            {isSubmitted ? (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Submitted
              </Badge>
            ) : (
              <Badge variant="default" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Not Submitted
              </Badge>
            )}
            {status.revision_cycle_count > 0 && (
              <Badge variant="info">
                Revision Cycle {status.revision_cycle_count}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Submission / Re-submit error banner - show API detail and clear next steps */}
      {(submitError || reSubmitError) && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-red-900 dark:text-red-100 mb-1">Submission could not be completed</p>
              <p className="text-sm text-red-800 dark:text-red-200 mb-2">{submitError ?? reSubmitError}</p>
              {isPreSubmissionValidationError(submitError ?? reSubmitError ?? '') && (
                <div className="text-sm text-red-800 dark:text-red-200 mb-3">
                  <p className="mb-1"><strong>What to do:</strong></p>
                  <p>Go to the <strong>Quality Assurance</strong> tab, run <strong>Run Pre-Submission Validation</strong>, address any issues, then try submitting again.</p>
                  {onSwitchToQATab && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setSubmitError(null);
                        setReSubmitError(null);
                        onSwitchToQATab();
                      }}
                    >
                      <FileCheck className="h-4 w-4 mr-2" />
                      Go to QA tab
                    </Button>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setSubmitError(null); setReSubmitError(null); }}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-800/40 text-red-600 dark:text-red-400 shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Pre-submission Validation Status */}
      {!validation.ready && validation.errors.length === 0 && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-center">
          <p className="text-sm text-blue-900 dark:text-blue-100 mb-3">
            Click "Run Pre-Submission Validation" to check all QA requirements
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={handleValidate}
            disabled={validateMutation.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${validateMutation.isPending ? 'animate-spin' : ''}`} />
            {validateMutation.isPending ? 'Validating...' : 'Run Pre-Submission Validation'}
          </Button>
        </div>
      )}

      {/* Validation Status - Informational Only (Not Blocking) */}
      {validation.errors.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Pre-Submission Validation Issues</p>
              <p className="text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                These issues are recommended to address before submission, but will not block submission.
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
                {validation.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Warnings</p>
              <ul className="list-disc list-inside text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
                {validation.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submission Form */}
      {!isSubmitted && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Review Type</label>
              <Select
                value={selectedReviewType}
                onChange={(e) => {
                  const newType = e.target.value as ReviewType;
                  setSelectedReviewType(newType);
                  setSelectedReviewer(''); // Reset reviewer when type changes
                  // Invalidate reviewers query to refetch for new review type
                  queryClient.invalidateQueries({ queryKey: ['available-reviewers', reportId, newType] });
                }}
                options={Object.entries(reviewTypes).map(([key, value]: [string, any]) => ({
                  value: key,
                  label: value.name || key,
                }))}
              />
              {reviewTypes[selectedReviewType] && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {reviewTypes[selectedReviewType].description}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Assign Reviewer (Optional)</label>
              {(() => {
                // Debug logging
                if (reviewersLoading) {
                  console.log('[Review Submission] Reviewers loading...', { reportId, selectedReviewType, isSubmitted });
                } else if (reviewersError) {
                  console.error('[Review Submission] Reviewers error:', reviewersError);
                } else if (availableReviewers) {
                  console.log('[Review Submission] Reviewers loaded:', availableReviewers.length, availableReviewers);
                } else {
                  console.log('[Review Submission] No reviewers data', { reportId, selectedReviewType, isSubmitted, enabled: !!reportId && !!selectedReviewType && !isSubmitted });
                }
                
                return reviewersLoading ? (
                  <div className="flex items-center gap-2 p-2">
                    <LoadingSpinner />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Loading reviewers...</span>
                  </div>
                ) : reviewersError ? (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
                    Failed to load reviewers: {reviewersError.message || 'Unknown error'}. Please check console for details.
                  </div>
                ) : availableReviewers && availableReviewers.length > 0 ? (
                  <Select
                    value={selectedReviewer}
                    onChange={(e) => setSelectedReviewer(e.target.value)}
                    options={[
                      { value: '', label: 'No Assignment' },
                      ...availableReviewers.map((reviewer: AvailableReviewer) => ({
                        value: reviewer.id,
                        label: `${reviewer.full_name || reviewer.username}${reviewer.expertise && reviewer.expertise.length > 0 ? ` (${reviewer.expertise.join(', ')})` : ''}`,
                      })),
                    ]}
                  />
                ) : (
                  <div className="space-y-2">
                    <Select
                      value=""
                      options={[{ value: '', label: 'No reviewers available' }]}
                      disabled
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Query enabled: {String(!!reportId && !!selectedReviewType && !isSubmitted)} | 
                      Report ID: {reportId ? 'Yes' : 'No'} | 
                      Review Type: {selectedReviewType || 'None'} | 
                      Submitted: {String(isSubmitted)}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Submission Notes (Optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={submissionNotes}
              onChange={(e) => setSubmissionNotes(e.target.value)}
              placeholder="Add any notes about this submission..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
            >
              <Send className="h-4 w-4 mr-2" />
              {submitMutation.isPending ? 'Submitting...' : 'Submit for Review'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleValidate}
              disabled={validateMutation.isPending}
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Validate Readiness
            </Button>
          </div>
        </div>
      )}

      {/* Re-submission Form */}
      {isSubmitted && isReadyForReSubmit && (
        <div className="space-y-4">
          {!showReSubmitForm ? (
            <Button
              variant="primary"
              onClick={() => setShowReSubmitForm(true)}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-submit After Revisions
            </Button>
          ) : (
            <div className="space-y-4 p-4 bg-theme-surface rounded-lg border border-theme-border">
              <h4 className="font-medium">Re-submission</h4>
              <div>
                <label className="block text-sm font-medium mb-2">Revision Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={revisionNotes}
                  onChange={(e) => setRevisionNotes(e.target.value)}
                  placeholder="Describe the revisions made..."
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowReSubmitForm(false);
                    setRevisionNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleReSubmit}
                  disabled={reSubmitMutation.isPending}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {reSubmitMutation.isPending ? 'Re-submitting...' : 'Re-submit'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Submission History */}
      {status && isSubmitted && (
        <div className="mt-6 pt-6 border-t border-theme-border">
          <h4 className="font-medium mb-3">Submission History</h4>
          <div className="space-y-2 text-sm">
            {status.submitted_at && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Submitted:</span>
                <span>{new Date(status.submitted_at).toLocaleString()}</span>
              </div>
            )}
            {status.submitted_by && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Submitted by:</span>
                <span>{status.submitted_by}</span>
              </div>
            )}
            {status.last_reviewed_at && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Last reviewed:</span>
                <span>{new Date(status.last_reviewed_at).toLocaleString()}</span>
              </div>
            )}
            {status.last_reviewed_by && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Last reviewed by:</span>
                <span>{status.last_reviewed_by}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
